import * as PropTypes from 'prop-types';
import React from 'react';

import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import getUserFacingPolicies from 'auth/getUserFacingPolicies';
import {getAdminDataPolicy, getDefaultDataPolicy} from 'auth/IntrinsicPolicies';
import {Collaborator, COLLABORATOR_SHAPE, Owner, OwnerInfo, PolicyType} from 'auth/PermissionConstants';
import {DATA_POLICY_SHAPE, DataPolicy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import CollaboratorsSection, {UpdateArgs} from 'collaborator-components/CollaboratorsSection';
import DataConnector from 'connect/DataConnector';
import * as RegistryConstants from 'constants/RegistryConstants';
import {Registry} from 'constants/RegistryConstants';
import {FullSource} from 'constants/SourceConstants';
import SourceDAO, {UpdateSourceResponse} from 'file-browser/SourceDAO';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import notifications, {Response} from 'notifications';
import {getStore} from 'redux-stores/Store';
import {
  DataModsItemType,
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface RegistryCollaboratorsSectionProps {
  registry: Registry & Pick<FullSource, 'permissions'>;
}

interface EnhancedProps {
  collaborators: Array<Collaborator>;
  dataPolicies: Array<DataPolicy>;
}

interface State {
  collaborators: Array<Collaborator>;
  isSubmitting: boolean;
  owner: Owner;
  ownerInfo: OwnerInfo;
}

class RegistryCollaboratorsSectionConnector extends DataConnector<
  RegistryCollaboratorsSectionProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    return {
      collaborators: this.getOrFetchRequired(
        ManagedModelType.COLLABORATORS_BY_SOURCE_ID,
        this.props.registry.id
      ),
      dataPolicies: getUserFacingPolicies(this.reduxState().models.policies.dataPolicies!),
    };
  }
}

export class RegistryCollaboratorsSection extends React.Component<
  RegistryCollaboratorsSectionProps & EnhancedProps,
  State
> {
  private _isMounted: boolean;

  constructor(props: RegistryCollaboratorsSectionProps & EnhancedProps) {
    super(props);
    this._isMounted = true;
    this.state = {
      collaborators: props.collaborators,
      isSubmitting: false,
      ownerInfo: props.registry.ownerInfo,
      owner: props.registry.s_owner,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    collaborators: PropTypes.arrayOf(COLLABORATOR_SHAPE).isRequired,
    registry: RegistryConstants.REGISTRY_SHAPE,
    dataPolicies: PropTypes.arrayOf(DATA_POLICY_SHAPE.isRequired), // via connect
  };

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps: RegistryCollaboratorsSectionProps & EnhancedProps): void {
    if (nextProps.registry.id !== this.props.registry.id) {
      this.setState({
        collaborators: nextProps.collaborators,
        ownerInfo: nextProps.registry.ownerInfo,
        owner: nextProps.registry.s_owner,
      });
    }
  }

  _cancelSubmitAction(): void {
    if (this._isMounted) {
      this.setState({isSubmitting: false});
    }
  }

  _handleSubmit = (): void => {
    this.setState({isSubmitting: true});
    callWithDataModifications({
      changeSummary: getInventoryRegistryChangeSummary({
        itemCounts: {[DataModsItemType.REGISTRY]: 1},
        action: InventoryRegistryDataModAction.MODIFY,
      }),
      onModalCancel: () => {
        this._cancelSubmitAction();
      },
      onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
        SourceDAO.update(
          this.props.registry.id,
          {
            ownerInfo: this.state.ownerInfo,
            collaborators: this.state.collaborators.filter(({id}) => id !== this.state.owner.id),
          },
          dataModsHeader
        )
          .then(({collaborators}: UpdateSourceResponse) => {
            getStore().dispatch(
              modelFetched(ManagedModelType.COLLABORATORS_BY_SOURCE_ID, {
                [this.props.registry.id]: collaborators,
              })
            );
            notifications.success(`Successfully updated collaborators.`);
          })
          .catch((response: Response) => {
            notifications.notifyError(response);
          })
          .finally(() => {
            this._cancelSubmitAction();
          });
      },
      apiIds: [this.props.registry.id],
      analyticsParams: {
        module: 'REGISTRY',
        // @ts-expect-error TODO: Should be using ActionOrigin type
        actionOrigin: 'registry_settings_general_update_collaborators',
        modifiedAction: 'REGISTRY_SETTINGS_COLLABORATORS',
        numModifiedTotalItems: 1,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
      // Hard code useDataModsIfEnabledAnywhere to true to
      // enable data mods when project level data mods are enabled
      useDataModsIfEnabledAnywhere: true,
    });
  };

  _handleUpdate = (data: UpdateArgs): void => {
    if (data.collaborators) {
      this.setState({collaborators: data.collaborators});
    }
    if (data.ownerInfo) {
      this.setState({ownerInfo: data.ownerInfo});
    }
    if (data.owner) {
      this.setState({owner: data.owner});
    }
  };

  _getAdminDataPolicyId = (dataPolicies: Array<DataPolicy>): string => getAdminDataPolicy(dataPolicies).id;

  _getDefaultDataPolicyId = (dataPolicies: Array<DataPolicy>): string =>
    getDefaultDataPolicy(dataPolicies).id;

  render(): JSX.Element {
    const isReadOnly = !this.props.registry.permissions.admin;
    return (
      <div>
        <CollaboratorsSection
          getAdminPolicyId={this._getAdminDataPolicyId}
          getDefaultPolicyId={this._getDefaultDataPolicyId}
          collaborators={this.state.collaborators}
          enableAuditors={false}
          isReadOnly={isReadOnly}
          policeableId={this.props.registry.id}
          owner={this.state.owner}
          ownerInfo={this.state.ownerInfo}
          onUpdate={this._handleUpdate}
          policies={this.props.dataPolicies}
          policyType={PolicyType.DATA}
        />
        {!isReadOnly && (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            disabled={this.state.isSubmitting}
            onClick={this._handleSubmit}
            size={ButtonSize.MEDIUM}
            text="Update collaborators"
            dataTestAttrs={{component: 'RegistryCollaboratorsSection', element: 'updateButton'}}
          />
        )}
      </div>
    );
  }
}

export default RegistryCollaboratorsSectionConnector.connect(RegistryCollaboratorsSection);
